/**
 * The automated review of a section's Zoom transcripts for inappropriate
 * behaviour, the section counterpart of the office hours transcript review
 * (`OfficeHoursReviewSchema` in `@cip/core/officeHours`).
 *
 * One review per transcript file, since a section-week has one transcript per
 * recording part (a host who rejoins starts a new part):
 * `sections/{sectionId}/transcriptReviews/{transcriptFileId}`. Written only by
 * the Admin SDK; course staff read it, one section's at a time or a course's
 * across sections with a collection-group query on `courseId` and `status`.
 */
import {z} from 'zod';

import type {SectionId} from '../../../section/sectionApi';
import {SECTION_COLLECTION_PATH} from './sectionDocument';
import type {SectionRecordingsDocument} from './sectionRecordings';

/** Subcollection under a section doc holding its transcript reviews. */
export const SECTION_TRANSCRIPT_REVIEWS_SUBCOLLECTION =
  'transcriptReviews' as const;

/** The courses whose section transcripts are reviewed. */
export const SECTION_TRANSCRIPT_REVIEW_COURSE_IDS: readonly string[] = ['cipx'];

/**
 * Whether a course's section transcripts are reviewed.
 *
 * @param courseId - The course.
 * @return True for a course in {@link SECTION_TRANSCRIPT_REVIEW_COURSE_IDS}.
 * @throws Never.
 */
export function isSectionTranscriptReviewOn(courseId: string): boolean {
  return SECTION_TRANSCRIPT_REVIEW_COURSE_IDS.includes(courseId);
}

/**
 * Path of one transcript's review.
 *
 * @param sectionId - The section.
 * @param transcriptFileId - Zoom recording file id of the transcript.
 * @return The document path.
 * @throws Never.
 */
export function getSectionTranscriptReviewDocumentPath({
  sectionId,
  transcriptFileId,
}: {
  sectionId: SectionId;
  transcriptFileId: string;
}): string {
  return `${SECTION_COLLECTION_PATH}/${sectionId}/${SECTION_TRANSCRIPT_REVIEWS_SUBCOLLECTION}/${transcriptFileId}`;
}

/**
 * Outcome of a transcript review:
 * - `flagged`: the model judged something in the section inappropriate.
 * - `clear`: it found nothing.
 * - `failed`: the review could not be made (empty transcript, model error,
 *   unreadable reply); `error` says why.
 */
export const SectionTranscriptReviewStatusSchema = z.enum([
  'flagged',
  'clear',
  'failed',
]);

export type SectionTranscriptReviewStatus = z.infer<
  typeof SectionTranscriptReviewStatusSchema
>;

/** One transcript's review. */
export const SectionTranscriptReviewSchema = z.object({
  /** The section's course, which the read rules check the reader's role in. */
  courseId: z.string(),
  /** The reviewed section. */
  sectionId: z.string(),
  /** 1-based section week the transcript is from. */
  sectionWeek: z.number().int().positive(),
  /** Who taught the section that week (its leader or cover); null if unknown. */
  teacherUid: z.string().nullable(),
  status: SectionTranscriptReviewStatusSchema,
  /** The model's explanation of a flag; empty when clear. */
  reasoning: z.string(),
  /** Why a `failed` review failed; absent otherwise. */
  error: z.string().optional(),
  /** Zoom recording file id of the transcript reviewed. */
  transcriptFileId: z.string(),
  /** ISO 8601 start of the recording part; null when Zoom gave none. */
  recordingStart: z.string().nullable(),
  /** The model that reviewed it. */
  model: z.string(),
  /** `TRANSCRIPT_MODERATION_PROMPT_VERSION` of the prompt used. */
  promptVersion: z.string(),
  /** When the review was written. */
  reviewedAtMs: z.number(),
});

export type SectionTranscriptReview = z.infer<
  typeof SectionTranscriptReviewSchema
>;

/** A transcript file named by a section-week's recordings doc. */
export interface SectionTranscriptFile {
  /** Zoom recording file id. */
  fileId: string;
  /** Object path in the recordings bucket (`sections/{id}/{weekIndex}/…`). */
  path: string;
  /** ISO 8601 start of the recording part; null when Zoom gave none. */
  recordingStart: string | null;
}

/**
 * Lists the transcript files of a section-week, earliest part first.
 *
 * @param recordings - The section-week's recordings doc.
 * @return The transcripts, ordered by recording start, then file id.
 * @throws Never.
 */
export function listSectionTranscriptFiles(
  recordings: SectionRecordingsDocument,
): SectionTranscriptFile[] {
  return Object.entries(recordings.renderings)
    .flatMap(([fileId, rendering]) =>
      rendering.fileType === 'TRANSCRIPT'
        ? [
            {
              fileId,
              path: rendering.path,
              recordingStart: rendering.recordingStart,
            },
          ]
        : [],
    )
    .sort(
      (a, b) =>
        (a.recordingStart ?? '').localeCompare(b.recordingStart ?? '') ||
        a.fileId.localeCompare(b.fileId),
    );
}
